/* eslint-disable  @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import store, { bfiUserActions, userActions } from '@apply/adminStore';
import { jwtDecode } from 'jwt-decode';
import { Environment } from '../../../store/src';
import { UserStore } from '@apply/store';
import dayjs from 'dayjs';
import { BaseApi } from './baseApi';

const options = {
    baseURL: import.meta.env.VITE_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
};

export interface IApiResponse {
    statusCode: number;
    errors: any[];
    result: any;
}

export interface ApiResponse<T> {
    statusCode: number;
    errors: any[];
    result: T;
}

interface Search<T> {
    totalCount: number;
    groupCount: number;
    data: T;
    summary: number[];
}

export interface SearchResponse<T> extends ApiResponse<Search<T>> {}

interface DataGrid<T> {
    data: T[];
    totalCount: number;
    summary: number[];
}

export interface DataGridGroup<T> {
    key: string;
    items: T[];
    count: number;
    summary: number[];
}

export interface DataGridGroupResponse<T> extends ApiResponse<DataGrid<DataGridGroup<T>>> {}

export interface IChangePasswordResult {
    succeeded: boolean;
    passwordTooShort: boolean;
    passwordRequiresSpecialCharacter: boolean;
    passwordRequiresMixedCase: boolean;
    passwordRequiresDigit: boolean;
}

export interface IApiChangePasswordResponse {
    statusCode: number;
    errors: any[];
    result: IChangePasswordResult;
}

export class BfiApi extends BaseApi {
    constructor(applicationInsights: ApplicationInsights) {
        super(applicationInsights);
        this.axiosInstance = axios.create(options);

        this.axiosInstance.interceptors.request.use(async (config) => {
            const applyAuth = UserStore.Types.retrieve();

            if (applyAuth?.token) {
                config.headers.Authorization = `Bearer ${applyAuth?.token}`;
            } else {
                const adminAuthCookie = store.getState().user['ffinuser'];
                if (adminAuthCookie && adminAuthCookie.length > 2) {
                    config.headers.Authorization = `Bearer ${adminAuthCookie}`;
                }
            }

            return config;
        });

        this.axiosInstance.interceptors.response.use(
            function (response) {
                return response;
            },
            async (error) => {
                const originalRequest = error.config;
                // if the request hits the server and we get a 401 unauthorized, try to refresh the token:
                if (error.response.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    const applyAuth = UserStore.Types.retrieve();
                    if (applyAuth?.token) {
                        //refresh apply token
                        if (applyAuth && UserStore.Types.isRegisteredUser(applyAuth)) {
                            applicationInsights.appInsights.trackTrace({
                                message: 'isRegisteredUser',
                            });
                            if (UserStore.Types.isExpired(applyAuth)) {
                                try {
                                    const applyRefreshed = await this.refreshApplyToken(applyAuth, applicationInsights);
                                    if (applyRefreshed) {
                                        const applyAccessToken = UserStore.Types.retrieve();
                                        if (applyAccessToken?.token) {
                                            originalRequest.headers.Authorization = `Bearer ${applyAccessToken.token}`;
                                            return this.axiosInstance(originalRequest);
                                        }
                                    }
                                } catch (error) {
                                    console.error('Error Refreshing Token');
                                    this.handleError(error);
                                }
                            } else {
                                applicationInsights.appInsights.trackTrace({
                                    message: 'NOT isExpired',
                                });
                                originalRequest.headers.Authorization = `Bearer ${applyAuth.token}`;
                            }
                        }
                    } else {
                        //refresh admin token
                        const adminAuthCookie = store.getState()?.user && store.getState()?.user['ffinuser'];
                        if (adminAuthCookie) {
                            try {
                                const refreshed = await this.refreshAdminToken(adminAuthCookie);
                                if (refreshed) {
                                    const adminAccessToken = store.getState().user['ffinuser'];
                                    originalRequest.headers.Authorization = `Bearer ${adminAccessToken}`;
                                    return this.axiosInstance(originalRequest);
                                }
                            } catch (error) {
                                console.error('Error Refreshing Token');
                                this.handleError(error);
                            }
                        }
                    }
                }
                return Promise.reject(error);
            }
        );
    }

    refreshAdminToken = async (cookie: any, refreshToken = null): Promise<boolean> => {
        const result = await this.axiosInstance
            .post('/account/refreshToken', {
                token: cookie,
                refreshToken: refreshToken || store.getState().user['refreshToken'],
            })
            .then((response) => {
                const data = response.data;
                if (data === 'Invalid Token' || data.err === 'Invalid Token') {
                    return false;
                } else {
                    const decoded: any = jwtDecode(data.token);
                    const user = {
                        success: true,
                        authenticated: true,
                        admin: data.user.isAdmin,
                        userId: data.user.userId,
                        companies: data.companies,
                        broker: data.broker,
                        isBroker: data.user.isBroker,
                        brokerId: data.user.brokerId,
                        ffinuser: data.token,
                        refreshToken: data.refreshToken,
                        expires: decoded.exp * 1000,
                        firstName: data.user.firstName,
                        lastName: data.user.lastName,
                        emailAddress: data.user.emailAddress,
                        requiresESign: data.requiresESign,
                        requiresAdminTermsESign: data.requiresAdminTermsESign,
                        statusCode: response.status,
                        featureList: data.featureList,
                        roles: data.roles,
                    };

                    store.dispatch(userActions.userUpdate(user) as never);
                    const loginDT = new Date().getTime();
                    store.dispatch(bfiUserActions.bfiUserUpdate(user, loginDT) as never);

                    return true;
                }
            });

        return result;
    };

    refreshApplyToken = async (applyAuth: any, applicationInsights: any, refreshToken = null): Promise<boolean> => {
        applicationInsights.appInsights.trackTrace({ message: 'isExpired' });
        // refresh the token
        const refreshHeaders = new Headers();
        refreshHeaders.append('Content-Type', 'application/json');
        const refreshRequestOptions: RequestInit = {
            method: 'POST',
            headers: refreshHeaders,
            body: JSON.stringify({
                token: applyAuth.token,
            }),
        };
        const baseUrl = import.meta.env.VITE_API_URL;
        const refreshResponse = await fetch(`${baseUrl}/account/refreshToken`, refreshRequestOptions);
        if (refreshResponse.ok) {
            applicationInsights.appInsights.trackTrace({
                message: 'refreshResponse.ok',
            });
            const refreshResult = await refreshResponse.text();
            if (refreshResult.includes('Invalid Token')) {
                applicationInsights.appInsights.trackTrace({
                    message: 'includes("Invalid Token")',
                });
                return false;
            } else {
                applicationInsights.appInsights.trackTrace({
                    message: 'DOES NOT include ("Invalid Token")',
                });
                const data = JSON.parse(refreshResult) as {
                    token: string;
                    refreshToken: string;
                };
                const decodedToken = jwtDecode<{ exp: number }>(data.token);
                const updatedAuth: UserStore.Types.Authentication = {
                    ...applyAuth,
                    token: data.token,
                    refreshToken: data.refreshToken,
                    expiry: decodedToken.exp * 1000,
                };
                applicationInsights.appInsights.trackTrace({ message: 'Auth UserStoreTypes.store refresh:' }, updatedAuth);
                UserStore.Types.store(updatedAuth);
                return true;
            }
        } else {
            applicationInsights.appInsights.trackTrace({
                message: 'NOT refreshResponse.ok',
            });
            return false;
        }
    };

    triggerFileDownload(dataFile: any, fileName: string) {
        const fileDownload = require('js-file-download');
        fileDownload(dataFile, fileName, 'application/pdf');
    }

    formatDateAsString(date: Date) {
        const dayjsDate = dayjs(date);
        if (!dayjsDate.isValid()) return null;

        return dayjsDate.format('YYYY/MM/DD');
    }
}
